import { AdminCourseDetail, CourseChanges } from "../../../../../models/CourseModels";

/** Preço digitado no padrão brasileiro ("197,50") para número. */
export const parsePrice = (value: string): number =>
  Number(value.trim().replace(",", "."));

/** Estado do formulário de curso. Tudo texto, como o usuário digita. */
export interface CourseFormState {
  title: string;
  description: string;
  price: string;
  sectionId: string;
  status: "Active" | "Inactive";
  comingSoon: boolean;
  priority: string;
  maxInstallments: string;
  pixPrice: string;
  pixDiscount: string; // sem o "%"
  subscription: boolean;
  subscriptionInstallments: string;
}

const numberToField = (value?: number | null) =>
  value === undefined || value === null ? "" : String(value).replace(".", ",");

/** `sectionid` é uma lista JSON ('["A","B"]'). Valor inválido vira lista vazia. */
export const parseSectionList = (sectionid?: string): string[] => {
  try {
    const list = JSON.parse(sectionid ?? "[]");
    return Array.isArray(list) ? list.filter((item) => typeof item === "string") : [];
  } catch {
    return [];
  }
};

/** Item de NL_Course → formulário. " " (vazio do backend) vira "". */
export const toFormState = (course: AdminCourseDetail): CourseFormState => ({
  title: course.title?.trim() ?? "",
  description: course.description?.trim() ?? "",
  price: numberToField(course.price),
  sectionId: parseSectionList(course.sectionid)[0] ?? "",
  status: course.status === "Inactive" ? "Inactive" : "Active",
  comingSoon: course.comingsoon === "true",
  priority: String(course.priority ?? 0),
  maxInstallments: numberToField(course.max_installments),
  pixPrice: numberToField(course.pix_price),
  pixDiscount: (course.pix_discount ?? "").replace("%", "").trim(),
  subscription: course.subscription === true,
  subscriptionInstallments: numberToField(course.subscription_installments),
});

const parseInteger = (value: string) => {
  const n = Number(value.trim());
  return Number.isInteger(n) ? n : NaN;
};

/**
 * Compara o formulário com o curso carregado e devolve só o que mudou, no
 * formato do POST /backoffice/course. Campo de pagamento esvaziado vira
 * `null` (remove o atributo). `otherSections` preserva as seções além da
 * primeira, que o formulário não mostra.
 */
export const buildCourseChanges = (
  initial: CourseFormState,
  current: CourseFormState,
  otherSections: string[] = []
): { changes: CourseChanges; errors: string[] } => {
  const changes: CourseChanges = {};
  const errors: string[] = [];
  const changed = (key: keyof CourseFormState) => initial[key] !== current[key];

  if (changed("title")) changes.title = current.title.trim();
  if (changed("description")) {
    changes.description = current.description.trim() || current.title.trim();
  }
  if (changed("price")) {
    const price = parsePrice(current.price);
    if (current.price.trim() === "" || isNaN(price) || price < 0) errors.push("Preço inválido");
    else changes.price = price;
  }
  if (changed("sectionId")) {
    changes.sectionid = JSON.stringify([
      current.sectionId,
      ...otherSections.filter((section) => section !== current.sectionId),
    ]);
  }
  if (changed("status")) changes.status = current.status;
  if (changed("comingSoon")) changes.comingsoon = current.comingSoon ? "true" : "false";
  if (changed("priority")) {
    const priority = parseInteger(current.priority);
    if (isNaN(priority) || priority < 0) errors.push("Prioridade precisa ser um número inteiro");
    else changes.priority = priority;
  }

  const optionalInteger = (
    key: "maxInstallments" | "subscriptionInstallments",
    field: "max_installments" | "subscription_installments",
    label: string
  ) => {
    if (!changed(key)) return;
    if (current[key].trim() === "") {
      changes[field] = null;
      return;
    }
    const n = parseInteger(current[key]);
    if (isNaN(n) || n < 1 || n > 24) errors.push(`${label} precisa ser um número de 1 a 24`);
    else changes[field] = n;
  };
  optionalInteger("maxInstallments", "max_installments", "Parcelas no cartão");
  optionalInteger("subscriptionInstallments", "subscription_installments", "Parcelas da assinatura");

  if (changed("pixPrice")) {
    if (current.pixPrice.trim() === "") changes.pix_price = null;
    else {
      const pix = parsePrice(current.pixPrice);
      if (isNaN(pix) || pix < 0) errors.push("Preço PIX inválido");
      else changes.pix_price = pix;
    }
  }
  if (changed("pixDiscount")) {
    if (current.pixDiscount.trim() === "") changes.pix_discount = null;
    else {
      const discount = parsePrice(current.pixDiscount.replace("%", ""));
      if (isNaN(discount) || discount < 0 || discount > 100) {
        errors.push("Desconto PIX precisa ser uma porcentagem de 0 a 100");
      } else changes.pix_discount = `${discount}%`;
    }
  }
  if (changed("subscription")) changes.subscription = current.subscription;

  return { changes, errors };
};
